// ============================================================
// CombatantSheetTabs - one combatant's own sheet view.
//
// This is the "unified experience of each character" made literal: the
// Overview, Defender, Stats and Gear tabs are the SAME components
// whether you are looking at your target or at yourself. They read a
// projected `Target` and a `Character`, both of which the roster
// produces for any combatant - so the only thing that differs between
// the two panels is which combatant is passed in.
//
// Deliberately prop-driven with no context reads. Each panel binds its
// own handlers (the target side to the target combatant, the actor side
// to whoever you are acting as), and this component stays a pure view.
// ============================================================
import TargetCharacteristicsTab from './TargetCharacteristicsTab';
import TargetDefenderTab from './TargetDefenderTab';
import TargetGearTab from './TargetGearTab';
import SheetEditor from './sheet/SheetEditor';

/**
 * One combatant's sheet tab, in the host panel's tab-content wrapper.
 *
 * @param {object} props
 * @param {string} props.tab               - 'characteristics' | 'defender' | 'stats' | 'gear'
 * @param {object} props.target            - projected Target for this combatant
 * @param {object|null} props.sourceCharacter
 * @param {boolean} props.locked
 * @param {string} props.className         - the host panel's tab-content class
 *
 * The rest are each tab's handlers and inputs: the Overview's rolls and
 * unnatural overrides, the Defender's mods, frenzy and standing reaction,
 * the defence-probability inputs (target side only - omitted when looking
 * at yourself, since there is no incoming attack to forecast), the Stats
 * sheet's `tone`, `sheetEditor` (useSheetEditor), `sheetLocked`,
 * `stateLocked`, `onTargetChange` and wrapper class, and the Gear's armour
 * and weapon-order writers.
 */
export default function CombatantSheetTabs(props) {
  const Sheet = SHEETS[props.tab];
  if (!props.target || !Sheet) return null;
  return (
    <div className={props.className}>
      <Sheet {...props} />
    </div>
  );
}

function OverviewSheet(p) {
  return (
    <TargetCharacteristicsTab
      target={p.target}
      sourceCharacter={p.sourceCharacter || null}
      onRollCharacteristic={p.onRollCharacteristic}
      onRollSkill={p.onRollSkill}
      onRollPlainTarget={p.onRollPlainTarget}
      onOpposedCharacteristic={p.onOpposedCharacteristic}
      onOpposedSkill={p.onOpposedSkill}
      onRollDiceExpr={p.onRollDiceExpr}
      unnaturalOverrides={p.unnaturalOverrides}
      onUnnaturalOverride={p.onUnnaturalOverride}
      applyConditionModifiers={p.applyConditionModifiers}
      condModOptions={p.condModOptions}
      blockedTests={p.blockedTests}
    />
  );
}

function DefenderSheet({ defenderCustomMods = [], locked = false, ...p }) {
  return (
    <TargetDefenderTab
      tone={p.tone}
      target={p.target}
      sourceCharacter={p.sourceCharacter || null}
      defenderCustomMods={defenderCustomMods}
      onAddMod={p.onAddMod}
      onEditMod={p.onEditMod}
      onRemoveMod={p.onRemoveMod}
      onToggleDefensiveStance={p.onToggleDefensiveStance}
      onToggleFrenzied={p.onToggleFrenzied}
      onSetStandingReaction={p.onSetStandingReaction}
      locked={locked}
      slot={p.slot}
      fireMode={p.fireMode}
      isMelee={p.isMelee}
      attackMod={p.attackMod}
      rangeBand={p.rangeBand}
      hatred={p.hatred}
      rerollDamage={p.rerollDamage}
      attackerCharStats={p.attackerCharStats}
      attackerConditions={p.attackerConditions}
      unnaturalDosMode={p.unnaturalDosMode}
      applyConditionModifiers={p.applyConditionModifiers}
      condModOptions={p.condModOptions}
      blockedTests={p.blockedTests}
    />
  );
}

/**
 * The live sheet. `sheetLocked` gates what the character IS, `stateLocked`
 * (the tab's `locked` when the host names none) what has happened to them.
 */
function StatsSheet({ locked = false, sheetLocked = false, ...p }) {
  return (
    <div className={p.editorWrapClassName}>
      <SheetEditor
        tone={p.tone}
        editor={p.sheetEditor}
        target={p.target}
        sheetLocked={sheetLocked}
        stateLocked={p.stateLocked ?? locked}
        onTargetChange={p.onTargetChange}
      />
    </div>
  );
}

function GearSheet({ locked = false, ...p }) {
  return (
    <TargetGearTab
      target={p.target}
      onEditArmour={p.onEditArmour}
      onReorderWeapons={p.onReorderWeapons}
      locked={locked}
    />
  );
}

/** Each tab key's sheet. */
const SHEETS = { characteristics: OverviewSheet, defender: DefenderSheet, stats: StatsSheet, gear: GearSheet };
